import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { formatBytes } from '@/shared/utils/formatBytes';
import { useDialog } from '@shared/hooks/useDialog';
import { Card, Button, Badge } from '@shared/components/ui';
import { Package, Upload, Trash2, CheckCircle, AlertTriangle, HardDrive } from 'lucide-react';

interface AgentBinary {
  id: string;
  version: string;
  platform: string;
  binary_type: string;
  filename: string;
  file_size: number;
  checksum_sha256: string;
  storage_path: string;
  is_active: boolean;
  uploaded_by?: string;
  notes?: string;
  created_at: string;
}

export default function BinaryManagement() {
  const { t } = useTranslation('admin');
  const { showError, showConfirm } = useDialog();
  const [binaries, setBinaries] = useState<AgentBinary[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const [hasSelectedFile, setHasSelectedFile] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadForm, setUploadForm] = useState({
    version: '',
    binary_type: 'agent' as 'agent' | 'installer',
    notes: '',
    activate: true,
  });

  const fetchBinaries = async (showPageLoading = true) => {
    if (showPageLoading) setLoading(true);
    try {
      const res = await api.get('/admin/agent-binaries');
      setBinaries(res.data || []);
      setError(null);
    } catch {
      setError(t('binaryManagement.errors.load'));
    } finally {
      if (showPageLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchBinaries();
  }, []);

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (uploading || actionPending || !file || !uploadForm.version.trim()) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('version', uploadForm.version.trim());
      formData.append('binary_type', uploadForm.binary_type);
      formData.append('notes', uploadForm.notes);
      if (uploadForm.activate) formData.append('activate', 'true');

      await api.post('/admin/agent-binaries?activate=' + uploadForm.activate, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Reset form.
      setUploadForm({ version: '', binary_type: 'agent', notes: '', activate: true });
      setHasSelectedFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchBinaries(false);
    } catch {
      await showError(
        t('binaryManagement.errors.uploadTitle'),
        t('binaryManagement.errors.upload')
      );
    } finally {
      setUploading(false);
    }
  };

  const handleActivate = async (id: string) => {
    if (actionPending || uploading) return;
    setActionPending(true);
    try {
      await api.put(`/admin/agent-binaries/${id}/activate`);
      await fetchBinaries(false);
    } catch {
      await showError(
        t('binaryManagement.errors.activateTitle'),
        t('binaryManagement.errors.activate')
      );
    } finally {
      setActionPending(false);
    }
  };

  const handleDelete = async (id: string, filename: string, version: string) => {
    if (actionPending || uploading) return;
    setActionPending(true);
    try {
      const confirmed = await showConfirm(
        t('binaryManagement.confirmDeleteTitle'),
        t('binaryManagement.confirmDeleteMessage', { filename, version }),
        'warning'
      );
      if (!confirmed) return;
      await api.delete(`/admin/agent-binaries/${id}`);
      await fetchBinaries(false);
    } catch {
      await showError(
        t('binaryManagement.errors.deleteTitle'),
        t('binaryManagement.errors.delete')
      );
    } finally {
      setActionPending(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="py-20 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
        </div>
      </>
    );
  }

  const agentBinaries = binaries.filter((b) => b.binary_type === 'agent');
  const installerBinaries = binaries.filter((b) => b.binary_type === 'installer');

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        {/* Header */}
        <div>
          <h1 className="app-page-title">{t('binaryManagement.title')}</h1>
          <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
            {t('binaryManagement.description')}
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="p-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark rounded-xl flex flex-col sm:flex-row sm:items-center gap-3 text-state-danger-light dark:text-state-danger-dark text-sm font-medium"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle size={16} /> {error}
            </div>
            <Button
              type="button"
              variant="outline"
              className="min-h-11 sm:ml-auto"
              onClick={() => fetchBinaries()}
            >
              {t('binaryManagement.errors.retry')}
            </Button>
          </div>
        )}

        {/* Upload Form */}
        <Card className="p-6">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <Upload size={20} /> {t('binaryManagement.uploadTitle')}
          </h2>
          <fieldset disabled={uploading || actionPending} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-4">
                <label
                  htmlFor="binary-file"
                  className="block text-xs font-bold text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider mb-1"
                >
                  {t('binaryManagement.file')} *
                </label>
                <input
                  id="binary-file"
                  ref={fileInputRef}
                  type="file"
                  accept=".exe"
                  required
                  aria-required="true"
                  onChange={(event) => setHasSelectedFile(Boolean(event.target.files?.length))}
                  className="w-full min-h-11 text-xs text-slate-600 dark:text-slate-300 file:mr-3 file:min-h-11 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-accent-soft-light file:text-accent-text-light hover:file:bg-accent-soft-dark dark:file:bg-accent-soft-dark dark:file:text-accent-text-dark cursor-pointer"
                />
              </div>
              <div>
                <label
                  htmlFor="binary-version"
                  className="block text-xs font-bold text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider mb-1"
                >
                  {t('binaryManagement.version')} *
                </label>
                <input
                  id="binary-version"
                  type="text"
                  placeholder={t('binaryManagement.versionPlaceholder')}
                  required
                  aria-required="true"
                  maxLength={50}
                  value={uploadForm.version}
                  onChange={(e) => setUploadForm({ ...uploadForm, version: e.target.value })}
                  className="w-full min-h-11 px-3 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label
                  htmlFor="binary-type"
                  className="block text-xs font-bold text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider mb-1"
                >
                  {t('binaryManagement.type')}
                </label>
                <select
                  id="binary-type"
                  value={uploadForm.binary_type}
                  onChange={(e) =>
                    setUploadForm({
                      ...uploadForm,
                      binary_type: e.target.value as 'agent' | 'installer',
                    })
                  }
                  className="w-full min-h-11 px-3 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="agent">{t('binaryManagement.agentType')}</option>
                  <option value="installer">{t('binaryManagement.installerType')}</option>
                </select>
              </div>
              <div className="flex items-end gap-2 lg:col-span-2">
                <label
                  htmlFor="binary-active"
                  className="flex min-h-11 items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer"
                >
                  <input
                    id="binary-active"
                    type="checkbox"
                    className="h-5 w-5 shrink-0 rounded border-border-strong-light dark:border-border-strong-dark"
                    checked={uploadForm.activate}
                    onChange={(e) => setUploadForm({ ...uploadForm, activate: e.target.checked })}
                  />
                  {t('binaryManagement.setActive')}
                </label>
                <Button
                  onClick={handleUpload}
                  disabled={uploading || !hasSelectedFile || !uploadForm.version.trim()}
                  className="ml-auto"
                >
                  {uploading ? t('binaryManagement.uploading') : t('binaryManagement.upload')}
                </Button>
              </div>
            </div>
            <div className="mt-3">
              <label
                htmlFor="binary-notes"
                className="block text-xs font-bold text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider mb-1"
              >
                {t('binaryManagement.notes')}
              </label>
              <input
                id="binary-notes"
                type="text"
                placeholder={t('binaryManagement.notesPlaceholder')}
                value={uploadForm.notes}
                onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                className="w-full min-h-11 px-3 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          </fieldset>
        </Card>

        {/* Binary Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Agent Binaries */}
          <Card className="p-6">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <HardDrive size={20} /> {t('binaryManagement.agentBinaries')}
            </h2>
            {error && agentBinaries.length === 0 ? null : agentBinaries.length === 0 ? (
              <p className="text-sm text-text-subtle-light dark:text-text-subtle-dark py-4 text-center">
                {t('binaryManagement.emptyAgent')}
              </p>
            ) : (
              <div className="space-y-3">
                {agentBinaries.map((b) => (
                  <BinaryRow
                    key={b.id}
                    binary={b}
                    disabled={uploading || actionPending}
                    onActivate={handleActivate}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </Card>

          {/* Installer Binaries */}
          <Card className="p-6">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <Package size={20} /> {t('binaryManagement.installerBinaries')}
            </h2>
            {error && installerBinaries.length === 0 ? null : installerBinaries.length === 0 ? (
              <p className="text-sm text-text-subtle-light dark:text-text-subtle-dark py-4 text-center">
                {t('binaryManagement.emptyInstaller')}
              </p>
            ) : (
              <div className="space-y-3">
                {installerBinaries.map((b) => (
                  <BinaryRow
                    key={b.id}
                    binary={b}
                    disabled={uploading || actionPending}
                    onActivate={handleActivate}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

function BinaryRow({
  binary,
  onActivate,
  onDelete,
  disabled,
}: {
  binary: AgentBinary;
  disabled: boolean;
  onActivate: (id: string) => void;
  onDelete: (id: string, filename: string, version: string) => void;
}) {
  const { t, i18n } = useTranslation('admin');
  return (
    <div className="flex items-center justify-between p-3 bg-surface-2-light dark:bg-surface-2-dark rounded-xl">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            title={binary.filename}
            className="text-sm font-bold text-slate-900 dark:text-white truncate"
          >
            {binary.filename}
          </span>
          <Badge variant={binary.is_active ? 'success' : 'neutral'} className="text-[10px]">
            {binary.is_active ? t('binaryManagement.active') : t('binaryManagement.inactive')}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-text-muted-light dark:text-text-muted-dark">
          <span className="min-w-0 break-all">v{binary.version}</span>
          <span>{formatBytes(binary.file_size, i18n.language)}</span>
          <span>{new Intl.DateTimeFormat(i18n.language).format(new Date(binary.created_at))}</span>
        </div>
        {binary.notes && (
          <p className="text-xs text-text-subtle-light dark:text-text-subtle-dark mt-1 truncate">
            {binary.notes}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1 ml-3">
        {!binary.is_active && (
          <button
            disabled={disabled}
            onClick={() => onActivate(binary.id)}
            className="min-h-11 min-w-11 inline-flex items-center justify-center hover:bg-state-ok-bg-light dark:hover:bg-state-ok-bg-dark rounded-lg text-text-subtle-light dark:text-text-subtle-dark hover:text-state-ok-light dark:hover:text-state-ok-dark transition-colors"
            title={t('binaryManagement.setActive')}
            aria-label={t('binaryManagement.activateVersion', { version: binary.version })}
          >
            <CheckCircle size={16} />
          </button>
        )}
        <button
          disabled={disabled}
          onClick={() => onDelete(binary.id, binary.filename, binary.version)}
          className="min-h-11 min-w-11 inline-flex items-center justify-center hover:bg-state-danger-bg-light dark:hover:bg-state-danger-bg-dark rounded-lg text-text-subtle-light dark:text-text-subtle-dark hover:text-state-danger-light dark:hover:text-state-danger-dark transition-colors"
          title={t('binaryManagement.delete')}
          aria-label={t('binaryManagement.deleteVersion', { version: binary.version })}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}
